/**
 * SidebarAllChats — grouped recent chats with infinite scroll.
 * Restores the chat-style date grouping used across the app shell.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Loader2, Trash2 } from 'lucide-react'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from '@/components/ui/sidebar'
import { useChatHistory } from '@/hooks/use-chat-history'
import { DeleteChatDialog } from '@/components/chat/delete-chat-dialog'
import { ChatRowIcon, ChatRowTitle, useChatRowLiveState } from './chat-row-live-state'
import { SUPER_AGENT_ID, isSuperAgentId } from '@taqat/core/super-agent'
import type { ChatHistoryApiItem, GroupedChatData } from '@taqat/core'

interface SidebarAllChatsProps {
  /** App ID to scope chat links and optionally filter chats by app agents */
  appId?: string
  /** Agent IDs that belong to the current app */
  agentIds?: string[]
  /** Whether the parent app agent list is still loading */
  appAgentsLoading?: boolean
  /** Chat ids to hide (desktop: CoWork tasks don't belong in the Chat list) */
  excludeChatIds?: string[]
}

/**
 * Human-readable section titles for grouped chat history.
 */
function getGroupDisplayName(groupKey: keyof GroupedChatData): string {
  switch (groupKey) {
    case 'today':
      return 'Today'
    case 'yesterday':
      return 'Yesterday'
    case 'lastWeek':
      return 'Last 7 days'
    case 'lastMonth':
      return 'Last 30 days'
    case 'older':
      return 'Older'
    default:
      return groupKey
  }
}

/**
 * Builds the correct chat detail path for both app-scoped and global chat shells.
 */
function getChatPath(chat: ChatHistoryApiItem, appId?: string): string {
  // Super-agent chats embed the canonical SUPER_AGENT_ID so the URL shape is
  // identical to specialist chats — `AgentChatDetailsPage` detects the
  // sentinel and delegates to the generic ChatDetailPage.
  const agentIdForUrl =
    chat.agentId && !isSuperAgentId(chat.agentId) ? chat.agentId : SUPER_AGENT_ID
  return `/app/${appId}/agents/${agentIdForUrl}/chat/${chat.id}`
}

/**
 * SidebarAllChats — restores grouped chat history with scroll pagination.
 */
export function SidebarAllChats({
  appId,
  agentIds = [],
  appAgentsLoading = false,
  excludeChatIds = [],
}: SidebarAllChatsProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const loadTriggerRef = useRef<HTMLDivElement>(null)
  const isLoadingMoreRef = useRef(false)

  const {
    groupedData,
    isLoading,
    isLoadingMore,
    isError,
    error,
    refetch,
    loadMore,
    hasMore,
  } = useChatHistory({
    limit: 30,
    autoFetch: true,
  })

  /**
   * Shared row decorations + the delete/navigate behaviour, so this list and
   * the member task peek render and animate identically.
   */
  const handleDeleted = useCallback(
    (deletedChatId: string) => {
      // Read window.location.pathname directly — agent new-chat updates the
      // URL via window.history.replaceState, which bypasses React Router's
      // useLocation, so React Router's snapshot can be stale when the user
      // deletes a freshly-created chat from the sidebar.
      const livePath = window.location.pathname
      if (livePath.includes(`/chat/${deletedChatId}`)) {
        const parentPath = livePath.replace(`/chat/${deletedChatId}`, '')
        navigate(parentPath || `/app/${appId ?? ''}`)
      }
      refetch()
    },
    [appId, navigate, refetch],
  )

  const {
    animatingTitles,
    streamingChatIds,
    deletingChatIds,
    iconOverrides,
    handleDeletingChange,
  } = useChatRowLiveState({ onDeleted: handleDeleted })

  const scopedAgentIds = useMemo(() => new Set(agentIds), [agentIds])
  const shouldFilterByAppAgents = Boolean(appId)
  const excludeSet = useMemo(() => new Set(excludeChatIds), [excludeChatIds])

  const visibleGroupedData = useMemo<GroupedChatData | null>(() => {
    if (!groupedData) return null
    if (shouldFilterByAppAgents && appAgentsLoading) return null

    const filterGroup = (items: ChatHistoryApiItem[]) =>
      items.filter((chat) => {
        if (excludeSet.has(chat.id)) return false
        if (!shouldFilterByAppAgents) return true
        return (
          !chat.agentId ||
          scopedAgentIds.has(chat.agentId) ||
          isSuperAgentId(chat.agentId)
        )
      })

    return {
      today: filterGroup(groupedData.today),
      yesterday: filterGroup(groupedData.yesterday),
      lastWeek: filterGroup(groupedData.lastWeek),
      lastMonth: filterGroup(groupedData.lastMonth),
      older: filterGroup(groupedData.older),
    }
  }, [appAgentsLoading, groupedData, scopedAgentIds, shouldFilterByAppAgents, excludeSet])

  const visibleChatCount = useMemo(() => {
    if (!visibleGroupedData) return 0

    return (
      visibleGroupedData.today.length +
      visibleGroupedData.yesterday.length +
      visibleGroupedData.lastWeek.length +
      visibleGroupedData.lastMonth.length +
      visibleGroupedData.older.length
    )
  }, [visibleGroupedData])

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore
  }, [isLoadingMore])

  useEffect(() => {
    const triggerElement = loadTriggerRef.current
    if (!triggerElement || !hasMore) return

    const scrollRoot = triggerElement.closest('[data-sidebar="content"]')
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasMore && !isLoadingMoreRef.current) {
          isLoadingMoreRef.current = true
          void loadMore()
        }
      },
      {
        root: scrollRoot instanceof Element ? scrollRoot : null,
        rootMargin: '120px 0px',
        threshold: 0.1,
      },
    )

    observer.observe(triggerElement)

    return () => observer.disconnect()
  }, [hasMore, loadMore, visibleChatCount])

  const isActive = (chatId: string) => location.pathname.includes(`/chat/${chatId}`)
  const showLoadingState = isLoading || appAgentsLoading
  const showEmptyState = !showLoadingState && !isError && visibleChatCount === 0 && !hasMore

  return (
    <SidebarGroup className="flex-1 py-1">
      <SidebarGroupContent>
        {showLoadingState ? (
          <SidebarMenu>
            {Array.from({ length: 4 }).map((_, index) => (
              <SidebarMenuItem key={`sidebar-chat-skeleton-${index}`}>
                <SidebarMenuSkeleton showIcon />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        ) : isError ? (
          <div className="px-3 py-2 text-sm text-muted-foreground group-data-[collapsible=icon]:hidden">
            {error || 'Failed to load chats'}
          </div>
        ) : showEmptyState ? (
          null
        ) : (
          <div className="space-y-3">
            {(Object.entries(visibleGroupedData ?? {}) as Array<[keyof GroupedChatData, ChatHistoryApiItem[]]>)
              .filter(([, chats]) => chats.length > 0)
              .map(([groupKey, chats]) => (
                <div key={groupKey}>
                  <div className="type-label px-3 pb-1.5 pt-1 text-muted-foreground/80">
                    {getGroupDisplayName(groupKey)}
                  </div>
                  <SidebarMenu>
                    {chats.map((chat) => (
                      <SidebarMenuItem key={chat.id}>
                        <SidebarMenuButton isActive={isActive(chat.id)} asChild>
                          <Link to={getChatPath(chat, appId)} className="flex min-w-0 items-center gap-3 pr-8">
                            <ChatRowIcon
                              isStreaming={streamingChatIds.has(chat.id)}
                              iconName={iconOverrides[chat.id] ?? chat.metadata?.icon ?? null}
                            />
                            <span className="truncate">
                              <ChatRowTitle
                                chatId={chat.id}
                                title={chat.title}
                                animatingTitles={animatingTitles}
                              />
                            </span>
                          </Link>
                        </SidebarMenuButton>
                        <DeleteChatDialog
                          chatId={chat.id}
                          onDeleted={() => {}}
                          onDeletingChange={(isDeleting) => handleDeletingChange(chat.id, isDeleting)}
                        >
                          <SidebarMenuAction
                            showOnHover={!deletingChatIds.has(chat.id)}
                            aria-label="Delete chat"
                            disabled={deletingChatIds.has(chat.id)}
                            className="text-muted-foreground hover:text-destructive data-[disabled]:opacity-100"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {deletingChatIds.has(chat.id)
                              ? <Loader2 className="animate-spin text-destructive" />
                              : <Trash2 />}
                          </SidebarMenuAction>
                        </DeleteChatDialog>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </div>
              ))}

            {hasMore && (
              <div
                ref={loadTriggerRef}
                className="h-1 w-full"
                style={{ minHeight: '1px' }}
              />
            )}

            {isLoadingMore && (
              <SidebarMenu>
                {Array.from({ length: 2 }).map((_, index) => (
                  <SidebarMenuItem key={`sidebar-chat-load-more-${index}`}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            )}

            {!isLoadingMore && !hasMore && visibleChatCount > 0 && (
              <div className="px-3 py-2 text-xs text-muted-foreground/70 group-data-[collapsible=icon]:hidden">
                End of chat history
              </div>
            )}
          </div>
        )}
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
